import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, RotateCcw, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import { onboardingAPI, workspaceAdminAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const emptyChecklistForm = {
  id: null,
  name: '',
  description: '',
  department: '',
  is_active: true,
};

const emptyItemForm = {
  key: '',
  name: '',
  description: '',
  category: 'general',
  assigned_to: 'HR',
  due_days: 7,
  is_mandatory: true,
};

const AdminOnboardingChecklistsPage = ({ dashboardHomePath = '/admin', scope = 'tenant' }) => {
  const navigate = useNavigate();
  // The superadmin master screen edits the template new workspaces are seeded
  // with. A tenant's own checklists are reachable only inside a proxy session,
  // which is why the tenant endpoint refuses here.
  const isGlobalScope = scope === 'global';
  const [rows, setRows] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checklistForm, setChecklistForm] = useState(emptyChecklistForm);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [items, setItems] = useState([]);

  const isEditMode = Boolean(checklistForm.id);
  const hasRows = rows.length > 0;
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [rows]
  );

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = isGlobalScope
        ? await workspaceAdminAPI.listGlobalChecklists()
        : await onboardingAPI.getChecklists({ include_inactive: includeInactive });
      setRows(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load onboarding checklists'));
    } finally {
      setLoading(false);
    }
  }, [includeInactive, isGlobalScope]);

  const resetBuilder = () => {
    setChecklistForm(emptyChecklistForm);
    setItemForm(emptyItemForm);
    setItems([]);
  };

  const applyChecklistToForm = (row) => {
    setChecklistForm({
      id: row.id,
      name: row.name || '',
      description: row.description || '',
      department: row.department || '',
      is_active: Boolean(row.is_active),
    });
    setItems(
      Array.isArray(row.items)
        ? row.items.map((item, index) => ({
            key: item.key || `item_${index + 1}`,
            name: item.name || '',
            description: item.description || '',
            category: item.category || 'general',
            assigned_to: item.assigned_to || 'HR',
            due_days: Number(item.due_days ?? item.days ?? 7),
            is_mandatory: item.is_mandatory !== false,
          }))
        : []
    );
    setItemForm(emptyItemForm);
  };

  const upsertItem = () => {
    const name = itemForm.name.trim();
    if (!name) {
      toast.error('Item name is required');
      return;
    }
    const key = (itemForm.key || name.toLowerCase().replace(/\s+/g, '_')).trim().toLowerCase();
    if (!key) {
      toast.error('Item key is invalid');
      return;
    }
    if (items.some((row) => row.key === key)) {
      toast.error(`Item key "${key}" already exists`);
      return;
    }
    setItems((prev) => [
      ...prev,
      {
        key,
        name,
        description: itemForm.description.trim() || null,
        category: itemForm.category,
        assigned_to: itemForm.assigned_to,
        due_days: Number(itemForm.due_days || 7),
        is_mandatory: Boolean(itemForm.is_mandatory),
      },
    ]);
    setItemForm(emptyItemForm);
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((row) => row.key !== key));
  };

  const saveChecklist = async (e) => {
    e.preventDefault();
    const name = checklistForm.name.trim();
    if (!name) {
      toast.error('Checklist name is required');
      return;
    }
    if (items.length === 0) {
      toast.error('Add at least one checklist item');
      return;
    }
    const payload = {
      name,
      description: checklistForm.description.trim() || null,
      department: checklistForm.department.trim() || null,
      items,
      is_active: Boolean(checklistForm.is_active),
    };
    try {
      setSaving(true);
      if (isEditMode) {
        const res = isGlobalScope
          ? await workspaceAdminAPI.updateGlobalChecklist(checklistForm.id, payload)
          : await onboardingAPI.updateChecklist(checklistForm.id, payload);
        const sync = res.data || {};
        toast.success(
          `Checklist updated. Synced ${sync.onboardings_updated || 0} onboarding runs, created ${sync.tasks_created || 0} tasks, skipped ${sync.tasks_skipped || 0} tasks.`
        );
      } else {
        if (isGlobalScope) {
          await workspaceAdminAPI.createGlobalChecklist(payload);
        } else {
          await onboardingAPI.createChecklist(payload);
        }
        toast.success('Checklist created');
      }
      resetBuilder();
      await loadRows();
    } catch (error) {
      toast.error(getErrorMessage(error, isEditMode ? 'Failed to update checklist' : 'Failed to create checklist'));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const toggleChecklist = async (row) => {
    try {
      if (isGlobalScope) {
        await workspaceAdminAPI.updateGlobalChecklist(row.id, { is_active: !row.is_active });
      } else {
        await onboardingAPI.updateChecklist(row.id, { is_active: !row.is_active });
      }
      await loadRows();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update checklist status'));
    }
  };

  const deleteChecklist = async (row) => {
    if (!window.confirm(`Deactivate checklist "${row.name}"?`)) return;
    try {
      if (isGlobalScope) {
        await workspaceAdminAPI.deleteGlobalChecklist(row.id);
      } else {
        await onboardingAPI.deleteChecklist(row.id);
      }
      toast.success('Checklist deactivated');
      await loadRows();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete checklist'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Onboarding Checklists</h2>
          <p className="text-sm text-muted-foreground">Production-grade checklist templates with structured task definitions and onboarding sync.</p>
        </div>
        <Button type="button" variant="outline" onClick={() => navigate(dashboardHomePath)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>{isEditMode ? `Edit Checklist: ${checklistForm.name}` : 'Create Checklist'}</CardTitle></CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={saveChecklist}>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label>Name</Label>
                <Input value={checklistForm.name} onChange={(e) => setChecklistForm((prev) => ({ ...prev, name: e.target.value }))} required />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={checklistForm.department} onChange={(e) => setChecklistForm((prev) => ({ ...prev, department: e.target.value }))} />
              </div>
              <div className="flex items-end">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="checklist-active"
                    checked={Boolean(checklistForm.is_active)}
                    onCheckedChange={(v) => setChecklistForm((prev) => ({ ...prev, is_active: Boolean(v) }))}
                  />
                  <Label htmlFor="checklist-active">Active</Label>
                </div>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={checklistForm.description} onChange={(e) => setChecklistForm((prev) => ({ ...prev, description: e.target.value }))} />
            </div>

            <div className="border rounded-md p-4 space-y-3">
              <p className="text-sm font-medium">Checklist Item Builder</p>
              <div className="grid md:grid-cols-6 gap-3">
                <div className="md:col-span-2">
                  <Label>Item Name</Label>
                  <Input value={itemForm.name} onChange={(e) => setItemForm((prev) => ({ ...prev, name: e.target.value }))} />
                </div>
                <div>
                  <Label>Key (optional)</Label>
                  <Input value={itemForm.key} onChange={(e) => setItemForm((prev) => ({ ...prev, key: e.target.value }))} />
                </div>
                <div>
                  <Label>Category</Label>
                  <Input value={itemForm.category} onChange={(e) => setItemForm((prev) => ({ ...prev, category: e.target.value }))} />
                </div>
                <div>
                  <Label>Assigned To</Label>
                  <Select value={itemForm.assigned_to} onValueChange={(value) => setItemForm((prev) => ({ ...prev, assigned_to: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HR">HR</SelectItem>
                      <SelectItem value="IT">IT</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                      <SelectItem value="Employee">Employee</SelectItem>
                      <SelectItem value="Admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Due (days)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={365}
                    value={itemForm.due_days}
                    onChange={(e) => setItemForm((prev) => ({ ...prev, due_days: Number(e.target.value || 0) }))}
                  />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={itemForm.description} onChange={(e) => setItemForm((prev) => ({ ...prev, description: e.target.value }))} />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="item-mandatory"
                    checked={Boolean(itemForm.is_mandatory)}
                    onCheckedChange={(v) => setItemForm((prev) => ({ ...prev, is_mandatory: Boolean(v) }))}
                  />
                  <Label htmlFor="item-mandatory">Mandatory</Label>
                </div>
                <Button type="button" variant="outline" onClick={upsertItem}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </div>
            </div>

            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Assigned</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Mandatory</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-6">No checklist items added yet.</TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>
                          <p className="font-medium">{row.name}</p>
                          <p className="text-xs text-muted-foreground">{row.key}</p>
                        </TableCell>
                        <TableCell>{row.category}</TableCell>
                        <TableCell>{row.assigned_to}</TableCell>
                        <TableCell>{row.due_days} days</TableCell>
                        <TableCell>{row.is_mandatory ? 'Yes' : 'No'}</TableCell>
                        <TableCell>
                          <Button size="icon" variant="outline" onClick={() => removeItem(row.key)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={saving || loading}>
                {saving ? 'Saving...' : isEditMode ? 'Update Checklist' : 'Create Checklist'}
              </Button>
              <Button type="button" variant="outline" onClick={resetBuilder}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Checklist Table</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox id="onboarding-checklist-include-inactive" checked={includeInactive} onCheckedChange={(v) => setIncludeInactive(Boolean(v))} />
            <Label htmlFor="onboarding-checklist-include-inactive">Show Inactive</Label>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Linked Runs</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!hasRows ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">No checklists found.</TableCell>
                </TableRow>
              ) : sortedRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.department || '-'}</TableCell>
                  <TableCell>{row.items_count ?? (Array.isArray(row.items) ? row.items.length : 0)}</TableCell>
                  <TableCell>{row.linked_onboardings ?? 0}</TableCell>
                  <TableCell>
                    <Badge variant={row.is_active ? 'success' : 'secondary'}>{row.is_active ? 'Active' : 'Inactive'}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button size="icon" variant="outline" title="Edit" onClick={() => applyChecklistToForm(row)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="outline" title="Delete" onClick={() => deleteChecklist(row)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="outline" title={row.is_active ? 'Deactivate' : 'Activate'} onClick={() => toggleChecklist(row)}>
                        {row.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOnboardingChecklistsPage;
